// Phase 5Z.9 — Fahrer-Portal Layout (mobile-first, bottom-nav)
import { useState } from "react";
import { Link, NavLink, Outlet, useNavigate, Navigate } from "react-router-dom";
import { Home, User, Truck, Clock, Calendar, LogOut, ClipboardList, Plane } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useFahrerAuth } from "./lib/auth";
import { fahrerSetLanguage } from "./lib/api";
import { SUPPORTED_LANGUAGES } from "./i18n";
import BlockingTaskGate from "./BlockingTaskGate";
import FahrerNotifBell from "./FahrerNotifBell";

export default function FahrerLayout() {
  const { me, loading, logout } = useFahrerAuth();
  const { t, i18n } = useTranslation();
  const [showLang, setShowLang] = useState(false);
  const nav = useNavigate();

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-zinc-400 text-sm">{t("app.loading")}</div>
      </div>
    );
  }
  if (!me) return <Navigate to="/fahrer/login" replace />;
  if (me.must_change_password) return <Navigate to="/fahrer/change-password" replace />;

  async function changeLang(code) {
    i18n.changeLanguage(code);
    try { await fahrerSetLanguage(code); } catch { /* ignore */ }
    setShowLang(false);
  }

  const currentLang = SUPPORTED_LANGUAGES.find(l => l.code === i18n.resolvedLanguage) || SUPPORTED_LANGUAGES[0];

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 flex flex-col" data-testid="fahrer-layout">
      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-zinc-950/95 backdrop-blur border-b border-zinc-800">
        <div className="max-w-md mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/fahrer" className="flex items-center gap-2.5">
            <div className="w-8 h-8 bg-amber-600 rounded-md flex items-center justify-center text-white font-bold text-sm">M</div>
            <div>
              <div className="text-sm font-semibold leading-tight">{t("app.name")}</div>
              <div className="text-[10px] text-zinc-500 leading-tight">{me.first_name} {me.last_name}</div>
            </div>
          </Link>
          <div className="flex items-center gap-1">
            <FahrerNotifBell />
            <Link to="/fahrer/profile" data-testid="fahrer-header-profile" className="p-2 rounded text-zinc-400 hover:text-amber-400 hover:bg-zinc-900" aria-label={t("nav.profile")}>
              <User className="w-4 h-4" />
            </Link>
            <button
              onClick={() => setShowLang(v => !v)}
              data-testid="fahrer-lang-toggle"
              className="p-2 rounded text-zinc-400 hover:text-amber-400 hover:bg-zinc-900"
              aria-label={t("nav.language")}
            >
              <span className="text-base">{currentLang.flag}</span>
            </button>
            <button
              onClick={async () => { await logout(); nav("/fahrer/login"); }}
              data-testid="fahrer-logout-btn"
              className="p-2 rounded text-zinc-400 hover:text-red-400 hover:bg-zinc-900"
              aria-label={t("auth.logout")}
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
        {showLang && (
          <div className="absolute top-14 right-3 bg-zinc-900 border border-zinc-800 rounded-lg shadow-xl py-1 min-w-[140px] z-40" data-testid="fahrer-lang-menu">
            {SUPPORTED_LANGUAGES.map(l => (
              <button
                key={l.code}
                onClick={() => changeLang(l.code)}
                data-testid={`fahrer-lang-${l.code}`}
                className={`w-full px-3 py-2 text-left text-sm hover:bg-zinc-800 flex items-center gap-2 ${i18n.resolvedLanguage === l.code ? "text-amber-400 font-medium" : "text-zinc-200"}`}
              >
                <span>{l.flag}</span>
                {l.label}
              </button>
            ))}
          </div>
        )}
      </header>

      {/* Content */}
      <main className="flex-1 max-w-md mx-auto w-full px-4 py-5 pb-28" data-testid="fahrer-main">
        <Outlet />
        <div className="mt-8 text-center text-[10px] text-zinc-700 italic tracking-wider" data-testid="fahrer-developed-by">
          Munja Trans GmbH · Entwicklung bei <span className="text-amber-500/70 not-italic font-semibold">Ichkov</span>
        </div>
      </main>

      {/* Phase 30 — Blocking gate for pending fahrer-tasks */}
      <BlockingTaskGate />

      {/* Bottom navigation (mobile-first) */}
      <nav className="fixed bottom-0 left-0 right-0 bg-zinc-900/95 backdrop-blur border-t border-zinc-800 z-30">
        <div className="max-w-md mx-auto px-1 py-1.5 grid grid-cols-6 gap-0.5">
          <BottomTab to="/fahrer" exact icon={Home} label={t("nav.dashboard")} testid="fahrer-nav-home" />
          <BottomTab to="/fahrer/vehicle" icon={Truck} label={t("nav.vehicle")} testid="fahrer-nav-vehicle" />
          <BottomTab to="/fahrer/zeit" icon={Clock} label={t("zeit.title")} testid="fahrer-nav-zeit" />
          <BottomTab to="/fahrer/hr" icon={Plane} label="Urlaub" testid="fahrer-nav-hr" />
          <BottomTab to="/fahrer/calendar" icon={Calendar} label={t("nav.schedule")} testid="fahrer-nav-calendar" />
          <BottomTab to="/fahrer/aufgaben" icon={ClipboardList} label="Aufgaben" testid="fahrer-nav-aufgaben" />
        </div>
      </nav>
    </div>
  );
}

function BottomTab({ to, exact, icon: Icon, label, testid }) {
  return (
    <NavLink
      to={to}
      end={exact}
      data-testid={testid}
      className={({ isActive }) => `flex flex-col items-center gap-0.5 py-2 rounded transition-colors ${isActive ? "text-amber-400" : "text-zinc-500 hover:text-zinc-200"}`}
    >
      <Icon className="w-5 h-5" />
      <span className="text-[10px] font-medium">{label}</span>
    </NavLink>
  );
}
